/* =========================================================================
   MAPA DE NAVEGACIÓN
   Los grupos no son decoración: separan los dominios reales del negocio
   (operación diaria, catálogo, la red de personas, el dinero y el control),
   que es como un administrador busca las cosas. Un listado plano de 14
   entradas obligaría a leerlas todas cada vez.
   ======================================================================== */

/*
 * `modulo` enlaza cada entrada con el catálogo del servidor
 * (App\Support\PanelModules). Es lo que permite recortar el menú según el área
 * de quien mira, y la clave tiene que coincidir EXACTAMENTE con la del backend:
 * si no coincide, la entrada desaparece del menú para todo el mundo.
 */
export const NAV = [
  {
    group: null,
    items: [
      { to: "/", label: "Panel", icon: "panel", end: true, modulo: "panel" },
    ],
  },
  {
    group: "Operación",
    items: [
      { to: "/ordenes", label: "Órdenes", icon: "receipt", modulo: "ordenes" },
      {
        to: "/domiciliarios",
        label: "Domiciliarios",
        icon: "bike",
        modulo: "domiciliarios",
      },
      { to: "/pagos", label: "Pagos", icon: "card", modulo: "pagos" },
      {
        to: "/comprobantes",
        label: "Comprobantes",
        icon: "receipt",
        modulo: "facturas",
      },
      {
        to: "/liquidaciones",
        label: "Liquidaciones",
        icon: "wallet",
        modulo: "liquidaciones",
      },
      // Va junto a Liquidaciones porque se leen juntas: el saldo de efectivo
      // es lo que explica que un corte de domiciliario salga en rojo.
      { to: "/efectivo", label: "Efectivo", icon: "card", modulo: "efectivo" },
    ],
  },
  {
    // Grupo propio: SST responde a otra pregunta que el resto del panel. No
    // "cómo va la operación" sino "está la gente en condiciones de operar".
    group: "SST",
    items: [
      {
        to: "/sst/documentos",
        label: "Documentación",
        icon: "shield",
        modulo: "sst.documentos",
      },
      {
        to: "/sst/incidentes",
        label: "Incidentes",
        icon: "alert",
        modulo: "sst.incidentes",
      },
    ],
  },
  {
    group: "Catálogo",
    items: [
      { to: "/negocios", label: "Negocios", icon: "store", modulo: "negocios" },
      {
        to: "/productos",
        label: "Productos",
        icon: "box",
        modulo: "productos",
      },
      {
        to: "/categorias",
        label: "Categorías",
        icon: "tag",
        modulo: "categorias",
      },
      // Dos taxonomías distintas, dos entradas: `category` clasifica productos
      // y `category_business` agrupa tiendas. Compartían pantalla detrás de una
      // pestaña y la segunda no se encontraba.
      {
        to: "/categorias-negocio",
        label: "Cat. de negocio",
        icon: "store",
        modulo: "categorias-negocio",
      },
    ],
  },
  {
    // Grupo propio y no una entrada dentro de "Catálogo": la pauta la maneja
    // quien vende publicidad, no quien mantiene los productos, y son dos
    // personas distintas buscando cosas distintas.
    group: "Marketing",
    items: [
      {
        to: "/marketing",
        label: "Resumen",
        icon: "megaphone",
        end: true,
        modulo: "marketing",
      },
      {
        to: "/marketing/anunciantes",
        label: "Anunciantes",
        icon: "building",
        modulo: "marketing.anunciantes",
      },
      {
        to: "/marketing/campanas",
        label: "Campañas",
        icon: "chart",
        modulo: "marketing.campanas",
      },
      {
        to: "/marketing/banners",
        label: "Banners",
        icon: "panel",
        modulo: "marketing.banners",
      },
      {
        to: "/marketing/cupones",
        label: "Cupones",
        icon: "ticket",
        modulo: "marketing.cupones",
      },
      {
        to: "/marketing/destacados",
        label: "Destacados",
        icon: "trophy",
        modulo: "marketing.destacados",
      },
      {
        to: "/marketing/notificaciones",
        label: "Notificaciones",
        icon: "bell",
        modulo: "marketing.notificaciones",
      },
    ],
  },
  {
    group: "Comunidad",
    items: [
      { to: "/usuarios", label: "Usuarios", icon: "users", modulo: "usuarios" },
      {
        to: "/propietarios",
        label: "Propietarios",
        icon: "building",
        modulo: "propietarios",
      },
      {
        to: "/conjuntos",
        label: "Conjuntos",
        icon: "pin",
        modulo: "conjuntos",
      },
      { to: "/resenas", label: "Reseñas", icon: "star", modulo: "resenas" },
      { to: "/chats", label: "Conversaciones", icon: "chat", modulo: "chats" },
      { to: "/pqrs", label: "PQRS", icon: "inbox", modulo: "pqrs" },
      {
        to: "/solicitudes",
        label: "Solicitudes de la web",
        icon: "ticket",
        modulo: "solicitudes",
      },
    ],
  },
  {
    group: "Control",
    items: [
      { to: "/reportes", label: "Reportes", icon: "chart", modulo: "reportes" },
      {
        to: "/auditoria",
        label: "Auditoría",
        icon: "shield",
        modulo: "auditoria",
      },
      {
        // Lo que se rompe en el telefono de la gente. Junto a Auditoria
        // porque responde la misma clase de pregunta: que paso.
        to: "/fallos",
        label: "Fallos de la app",
        icon: "alert",
        modulo: "fallos",
      },
      {
        to: "/areas",
        label: "Roles y accesos",
        icon: "shield",
        modulo: "areas",
        /*
         * La única entrada del menú que exige GESTIÓN, no solo vista.
         *
         * La pantalla ENTERA es la superficie de edición: su listado ya es el
         * editor de permisos, así que el servidor protege hasta el GET con
         * `modulo:areas,gestionar`. Mostrarla a quien solo consulta ofrecía una
         * sección que respondía 403 al abrirse.
         */
        exigeGestion: true,
      },
      { to: "/ajustes", label: "Ajustes", icon: "cog", modulo: "ajustes" },
    ],
  },
];

/**
 * El menú recortado a lo que puede ver un área.
 *
 * Los grupos que quedan sin entradas se eliminan: un encabezado "Marketing"
 * seguido de nada se lee como que la sección está rota, no como que no
 * corresponde.
 *
 * `puedeGestionar` es opcional para no romper a quien llame con un solo
 * argumento; sin él, las entradas marcadas `exigeGestion` se ocultan, que es el
 * lado seguro del error.
 */
export const navPara = (puedeVer, puedeGestionar = () => false) =>
  NAV.map((seccion) => ({
    ...seccion,
    items: seccion.items.filter((item) => {
      if (!item.modulo) return true;
      return item.exigeGestion
        ? puedeGestionar(item.modulo)
        : puedeVer(item.modulo);
    }),
  })).filter((seccion) => seccion.items.length > 0);

/**
 * La entrada del menú a la que corresponde una ruta.
 *
 * Gana la coincidencia más larga: "/categorias" también es prefijo de
 * "/categorias-negocio", y con el primer acierto la segunda ruta se resolvía
 * con el módulo de la primera.
 */
export const entradaDeRuta = (pathname) => {
  let mejor = null;

  for (const seccion of NAV) {
    for (const item of seccion.items) {
      if (item.to === "/") {
        if (pathname === "/") return item;
        continue;
      }
      if (
        pathname.startsWith(item.to) &&
        (!mejor || item.to.length > mejor.to.length)
      ) {
        mejor = item;
      }
    }
  }

  return mejor;
};

/** El módulo al que pertenece una ruta, para decidir si se puede entrar. */
export const moduloDeRuta = (pathname) =>
  entradaDeRuta(pathname)?.modulo ?? null;

/** Título legible por ruta, para la miga de pan del topbar. */
export const routeTitle = (pathname) => {
  if (pathname === "/") return "Panel";

  // Gana la coincidencia más larga: "/categorias" también es prefijo de
  // "/categorias-negocio", y con el primer acierto la segunda ruta se
  // anunciaba con el rótulo de la primera.
  let mejor = null;
  for (const section of NAV) {
    for (const item of section.items) {
      if (item.to === "/") continue;
      if (
        pathname.startsWith(item.to) &&
        (!mejor || item.to.length > mejor.to.length)
      ) {
        mejor = item;
      }
    }
  }

  return mejor?.label ?? "Administración";
};
